import {DateTime} from 'luxon';
import {DayTime, WeekDay} from '@domain/common/common';

export interface TimeDuration {
  negative: boolean;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export class TimeUtil {

  public static readonly DAYTIME_MIN = {
    [DayTime.Morning]: 4,
    [DayTime.Noon]: 11,
    [DayTime.Afternoon]: 12,
    [DayTime.Evening]: 17,
    [DayTime.Night]: 21
  };

  public static readonly DAYTIME_MAX = {
    [DayTime.Morning]: 12,
    [DayTime.Noon]: 14,
    [DayTime.Afternoon]: 18,
    [DayTime.Evening]: 23,
    [DayTime.Night]: 6
  };

  public static toDate(date: string | null | undefined, time: string | null | undefined = null, tz = 'Europe/Zurich'): Date | null {
    if (!date) {
      return null;
    }
    let timeStr = time ?? '12:00:00'; // default to midday, to ensure we have the right local date
    if (timeStr.length === 5) {
      timeStr = `${timeStr}:00`;
    }
    const dt = DateTime.fromFormat(
      `${date} ${timeStr}`,
      'yyyy-MM-dd HH:mm:ss',
      { zone: tz }
    );
    return dt.toJSDate();
  }

  public static toIsoDate(date: Date | number | null | undefined): string | null {
    if (!date) {
      return null;
    }
    return (new Date(date)).toISOString().split('T')[0];
  }

  public static fromIsoDateTime(iso: Date | string | null | undefined): Date | null {
    if (!iso) {
      return null;
    }
    const dte = new Date(iso); // create a new date copy, even if we already receive a Date object
    dte.setUTCSeconds(0, 0);
    return dte;
  }

  public static toSwissFormattedDate(date: Date | number | null): string | null {
    if (!date) {
      return null;
    }
    return (new Date(date)).toLocaleDateString("fr-CH", {timeZone: "Europe/Zurich"});
  }

  public static toSwissDate(date: Date | number | null): string | null {
    if (!date) {
      return null;
    }
    const swissFormatted =  this.toSwissFormattedDate(date);
    if (!swissFormatted) {
      return null;
    }
    const [day, month, year] = swissFormatted.split('.');
    return `${year}-${month}-${day}`;
  }

  public static toSwissTime(date: Date | number | null): string | null {
    if (!date) {
      return null;
    }
    return (new Date(date)).toLocaleTimeString("fr-CH", {timeZone: "Europe/Zurich"});
  }

  public static toDateString(dateString: string | null, timeString: string | null, requireDate = true, requireTime = false): Date | null {
    if (!((dateString || !requireDate) && (timeString || !requireTime))) {
      return null;
    }
    const date = dateString ?? (new Date()).toISOString().split('T')[0];
    // 12:00 to avoid DST issues
    const time = timeString ?? '12:00';
    return new Date(`${date}T${time}`);
  }

  public static toDuration(start: Date | number | null, end: Date | number | null): TimeDuration | null {
    let seconds = this.toSeconds(start, end, false);
    if (!seconds) {
      return null;
    }
    const isNegative = seconds < 0;
    if (isNegative) {
      seconds = Math.abs(seconds);
    }
    const days = Math.floor(seconds / (3600 * 24));
    seconds -= days * (3600 * 24);
    const hours = Math.floor(seconds / 3600);
    seconds -= hours * 3600;
    const minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;
    return {
      negative: isNegative,
      days: days,
      hours: hours,
      minutes: minutes,
      seconds: seconds
    };
  }

  public static toDurationString(duration: TimeDuration | null): string | null {
    if (!duration) {
      return null;
    }
    let str = '';
    if (duration.negative) {
      str += '-';
    }
    if (duration.days) {
      str += `${duration.days}j `;
    }
    if (duration.hours) {
      str += `${duration.hours}h `;
    }
    if (duration.minutes) {
      str += `${duration.minutes}m `;
    }
    return str;
  }

  public static addDays(days: number, reference: number = Date.now()): Date {
    const swissDateTime = DateTime.fromJSDate(new Date(reference), { zone: 'Europe/Zurich' });
    const newDate = swissDateTime.plus({ days: days });
    return newDate.toJSDate();
  }

  public static getNextWeekDay(refDate: Date | null | undefined, targetTime: string, dayOfWeek: number): Date | null {
    const isoDate = TimeUtil.toIsoDate(refDate)!;
    let refDateTime = TimeUtil.toDate(isoDate, targetTime);
    if (!refDateTime) {
      return null;
    }
    if (refDate && refDateTime.getTime() < refDate.getTime()) {
      // if targetTime is already in the past -> ensure we take the next one
      refDateTime = TimeUtil.addDays(1, refDateTime.getTime());
    }
    const daysDiff = dayOfWeek - refDateTime.getDay();
    const daysToAdd = daysDiff >= 0 ? daysDiff : 6 - daysDiff;
    return TimeUtil.addDays(daysToAdd, refDateTime.getTime());
  }

  private static readonly ONE_WEEK = 7 * 24 * 60 * 60 * 1000;
  public static weeksBetween(d1: Date | null, d2: Date | null, roundUp = true): number | null {
    if (!d1 || !d2) {
      return null;
    }
    const round = roundUp ? Math.ceil : Math.floor;
    return round((Math.abs(d2.getTime() - d1.getTime())) / this.ONE_WEEK);
  }

  public static nextSwissWeek(fromDateTime: Date, weeksToAdd = 1): Date {
    const luxonDate = DateTime.fromJSDate(fromDateTime, { zone: 'Europe/Zurich' });
    const nextWeek = luxonDate.plus({ weeks: weeksToAdd });
    return nextWeek.toJSDate();
  }

  // public static formatDate(date: Date | null): string | null {
  //   if (!date) {
  //     return null;
  //   }
  //   const day = date.getDate().toString(10).padStart(2, '0')
  //   const month = (date.getMonth() + 1).toString(10).padStart(2, '0');
  //   return `${day}.${month}.${date.getFullYear()}`;
  // }

  public static formatDate(isoDate: Date | string | null | undefined): string | null {
    if (!isoDate) {
      return null;
    }
    const [year, month, day] = isoDate instanceof Date
      ? [
        isoDate.getFullYear(),
        (isoDate.getMonth() + 1).toString(10).padStart(2, '0'),
        isoDate.getDate().toString(10).padStart(2, '0')
      ]
      : isoDate.split('-');
    return `${day}.${month}.${year}`;
  }

  public static formatTime(isoTime: string | null | undefined): string | null {
    if (!isoTime) {
      return null;
    }
    const [hours, minutes] = isoTime.split(':');
    return `${hours}:${minutes}`;
  }

  private static toSeconds(start: Date | number | null, end: Date | number | null, absolute = true): number | null {
    if (!start || !end) {
      return null;
    }
    const tStart = new Date(start);
    const tEnd = new Date(end);
    const timeDiff = tEnd.getTime() - tStart.getTime();
    const diff = absolute ? Math.abs(timeDiff) : timeDiff;
    return diff / 1000;
  }

  // public static toNumericDay(day: WeekDay): number {
  //   return Object.values(WeekDay).indexOf(day);
  // }
  //
  // public static toWeekDay(dayNum: number): WeekDay | null {
  //   if (dayNum < 0 || dayNum > 6) {
  //     return null;
  //   }
  //   return Object.values(WeekDay)[dayNum] as WeekDay;
  // }

}
